import { Card } from '@mui/material';
import classNames from 'classnames';
import { memo, PropsWithChildren, CSSProperties } from 'react';
import css from './Card.module.scss';
import { useLocalization } from '../../Contexts/Localization';
import { GMIconButton } from '../Buttons/IconButton';
import { Close } from '@mui/icons-material';

interface ComponentProps extends PropsWithChildren {
    /**
     * Callback when card is clicked. Will change the cursor to a pointer
     */
    onClick?: () => void;
    /**
     * Styles to apply
     */
    className?: string;
    /**
     * Whether to include a border around the card
     */
    border?: boolean;
}

export const GMCard: React.FC<ComponentProps> = memo((props) => {
    return (
        <Card
            className={classNames(css.root, {
                [css.clickable]: !!props.onClick,
                [css.border]: props.border,
            }, props.className)}
            onClick={props.onClick}
        >
            {props.children}
        </Card>
    );
});

export interface CardTitleProps extends PropsWithChildren {
    /**
     * If passed, will display a a close icon
     */
    onClose?: () => void;
    /**
     * Tooltip text for close icon. Default 'Close'
     */
    closeIconTooltipText?: string;
    /**
     * Reduces the height and title font size.
     */
    thin?: boolean;
    /**
     * Styles to apply
     */
    className?: string;
    /**
     * Overrides the background color of the header
     */
    color?: CSSProperties['backgroundColor'];
    /**
     * Disables the onClose icon button.
     */
    disableOnClose?: boolean;
}

export const GMCardHeader: React.FC<CardTitleProps> = memo((props) => {
    const Localization = useLocalization();

    return (
        <div className={classNames(css.header, props.onClose ? css.hasCloseBtn : '', props.thin ? css.thin : '')} style={{ backgroundColor: props.color }}>
            <div className={css.title}>
                {props.children}
            </div>
            {props.onClose ?
                <div>
                    <GMIconButton
                        disableRipple
                        color={'light'}
                        onClick={props.onClose}
                        tooltip={props.closeIconTooltipText ?? Localization.Close}
                        disabled={props.disableOnClose}
                    >
                        <Close />
                    </GMIconButton>
                </div> : null
            }
        </div>
    );
});

interface CardSubheaderProps extends PropsWithChildren {
    /**
     * Styles to apply
     */
    className?: string;
    /**
     * Gets displayed on the left of the subheader
     */
    leftElement?: JSX.Element;
    /**
     * Coloration of the subheader. Default 'themed'
     */
    variant?: 'themed' | 'warning';
}

export const GMCardSubHeader: React.FC<CardSubheaderProps> = memo(({ variant = 'themed', ...props }) => {
    return (
        <div
            className={classNames(css.subheader, {
                [css.noLeftPadding]: !!props.leftElement,
                [css.warning]: variant === 'warning',
                [css.themed]: variant === 'themed',
            }, props.className)}
        >
            {
                !!props.leftElement &&
                <div className={css.leftItem}>
                    {props.leftElement}
                </div>
            }
            <div>
                {props.children}
            </div>
        </div>
    );
});

interface CardBodyProps extends PropsWithChildren {
    /**
     * Removes padding
     */
    noPadding?: boolean;
    /**
     * Styles to apply
     */
    className?: string;
}

export const GMCardBody: React.FC<CardBodyProps> = memo((props) => {
    return (
        <div className={classNames(css.body, props.noPadding ? css.noPadding : '', props.className)}>
            {props.children}
        </div>
    );
});

export interface CardFooterProps extends PropsWithChildren {
    /**
     * Whether the top border will be dark (grey) or light (white). Default 'dark'
     */
    color?: 'light' | 'dark';
    /**
     * Styles to apply
     */
    className?: string;
}

export const GMCardFooter: React.FC<CardFooterProps> = memo(({ color = 'dark', ...props }) => {
    return (
        <div className={classNames(css.footer, color === 'dark' ? css.dark : css.light, props.className)}>
            {props.children}
        </div>
    );
});